/*
 * <BeMenu> -- the .be-menu column menu: items, separators, the marked check, the
 * submenu caret and Tracker's icon slot. Markup is a <div role="menu"> of <button>
 * items and <hr> separators. menu.scss opens and closes submenus; the only thing
 * here is the arrow keys. R5 tracked menus button-HELD, which this does not.
 */

import { isValidElement, type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { type BeProps, beComponent } from "./create.tsx";
import { type BeMenuModifier, MENU_MODIFIER_GLYPHS, MENU_MODIFIER_ORDER } from "./menu-glyphs.ts";
import { pixelText } from "./pixel-text.tsx";

export type { BeMenuModifier } from "./menu-glyphs.ts";

/**
 * A structured menu shortcut: one or more modifier keycaps plus the accelerator
 * key. Mirrors R5 BMenuItem::DrawShortcutSymbol, which blits a key-cap glyph per
 * modifier (the ⌘ cloverleaf / CTL / SHFT / OPT / ALT) followed by the letter. If
 * `modifiers` is omitted it defaults to `["command"]` — the R5 default for
 * `BMenuItem(label, msg, 'X')`.
 */
export type BeShortcut = {
	modifiers?: BeMenuModifier[];
	key: string;
};

function isShortcut(s: ReactNode | BeShortcut): s is BeShortcut {
	// isValidElement first: a keyed element has a string `key` and would duck-type in
	return typeof s === "object" && s !== null && !isValidElement(s) && typeof (s as BeShortcut).key === "string";
}

function ShortcutKeys(props: { shortcut: BeShortcut; disabled?: boolean }) {
	const { shortcut, disabled } = props;
	const asked = new Set(shortcut.modifiers ?? ["command"]);
	// R5's order, not the caller's -- and a set, since fModifiers is a bitmask
	const modifiers = MENU_MODIFIER_ORDER.filter((m) => asked.has(m));
	return (
		<span className="be-menu__keys">
			{modifiers.map((m) => {
				const g = MENU_MODIFIER_GLYPHS[m][disabled ? "disabled" : "normal"];
				return (
					<img
						key={m}
						className="be-menu__keycap"
						src={g.url}
						alt={m}
						style={{
							"--be-kc-w": g.width,
							"--be-kc-h": g.height,
						}}
					/>
				);
			})}
			{pixelText(shortcut.key, "be-menu__key")}
		</span>
	);
}

/** BMenu::InvokeItem's blink, which runs BEFORE the item is invoked. */
export const BE_MENU_INVOKE_MS = 200;

/** Direct item children of `menu`, in DOM order. A nested menu's own items are its. */
function itemsOf(menu: HTMLElement): HTMLButtonElement[] {
	return [...menu.querySelectorAll<HTMLButtonElement>(":scope > .be-menu__item, :scope > .be-menu__nest > .be-menu__item")];
}

/** The menu an event happened in -- NOT an ancestor of it, so only the innermost acts. */
function innermost(e: KeyboardEvent<HTMLElement>): boolean {
	return (e.target as HTMLElement).closest(".be-menu") === e.currentTarget;
}

export type BeMenuProps = BeProps<
	"div",
	{
		/** Menu items and separators. */
		children?: ReactNode;
	}
>;

export const BeMenu = beComponent<BeMenuProps, HTMLDivElement>("BeMenu", (props, be) => {
	const { scale, className, style, children, onKeyDown, ...rest } = props;
	// Up/Down here rather than on the item: the keydown bubbles from whichever item
	// holds focus, so one handler per menu covers them all without a registry.
	const keys = (e: KeyboardEvent<HTMLDivElement>) => {
		onKeyDown?.(e);
		if (e.defaultPrevented || !innermost(e)) return;
		const items = itemsOf(e.currentTarget).filter((i) => !i.disabled);
		const at = items.indexOf(document.activeElement as HTMLButtonElement);
		const step = (to: number) => {
			e.preventDefault();
			items[(to + items.length) % items.length]?.focus();
		};
		if (e.key === "ArrowDown") return step(at + 1);
		if (e.key === "ArrowUp") return step(at < 0 ? -1 : at - 1);
		if (e.key === "Home") return step(0);
		if (e.key === "End") return step(items.length - 1);
	};

	return (
		<div role="menu" onKeyDown={keys} {...rest} {...be("be-menu")}>
			{children}
		</div>
	);
});

export type BeMenuItemProps = BeProps<
	"button",
	{
		/** Force the selected/highlighted appearance. */
		selected?: boolean;

		/** Draw the check in the left mark column (BMenuItem::SetMarked). */
		marked?: boolean;

		/** A 16x16 mini icon; indents the label. The kit ships no art. */
		icon?: ReactNode;

		/** `true` draws the caret alone; the child's items make it open one. */
		submenu?: boolean | ReactNode;

		/** R5 blinks before invoking, so `onSelect` fires {@link BE_MENU_INVOKE_MS} late. `false` drops both. */
		blink?: boolean;

		/**
		 * Accelerator shown right-aligned. Either a plain node (e.g. "F", "Alt+Q") or
		 * a structured {@link BeShortcut} (`{ key: "F" }` or
		 * `{ modifiers: ["command", "shift"], key: "S" }`) which renders the real R5
		 * modifier keycaps.
		 */
		shortcut?: ReactNode | BeShortcut;

		/** Invoked on click; wired to the underlying button. */
		onSelect?: () => void;

		/** Item label. */
		children?: ReactNode;
	}
>;

export const BeMenuItem = beComponent<BeMenuItemProps, HTMLButtonElement>("BeMenuItem", (props, be) => {
	const {
		disabled,
		selected,
		marked,
		icon,
		submenu,
		blink,
		shortcut,
		onSelect,
		onKeyDown,
		scale,
		className,
		style,
		children,
		type: _type,
		...rest
	} = props;

	// role and aria-checked move together: menuitemcheckbox requires the state
	const mark = marked ? ({ role: "menuitemcheckbox", "aria-checked": true } as const) : ({ role: "menuitem" } as const);

	// InvokeItem blinks the item and only THEN invokes, so the menu has to outlive
	// the click. The blink itself is CSS; this is just R5's ordering.
	const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
	useEffect(() => () => clearTimeout(timer.current), []);

	const nest = useRef<HTMLDivElement>(null);
	// aria tracks focus, not hover -- for the keyboard user who reads it they agree
	const [entered, setEntered] = useState(false);

	const caret = submenu != null && submenu !== false;
	const child = submenu === true || !caret ? null : submenu;

	const focus = (sel: string) => nest.current?.querySelector<HTMLButtonElement>(sel)?.focus();

	const invoke = onSelect
		? () => {
				if (caret || blink === false) return onSelect();
				timer.current = setTimeout(onSelect, BE_MENU_INVOKE_MS);
			}
		: undefined;

	// focusing into the child is what opens it, and focusing back out shuts it
	const keys = (e: KeyboardEvent<HTMLButtonElement>) => {
		onKeyDown?.(e);
		if (e.defaultPrevented) return;
		if (child != null && e.key === "ArrowRight") {
			e.preventDefault();
			focus(".be-menu--sub .be-menu__item");
		} else if (e.key === "ArrowLeft" || e.key === "Escape") {
			const opener = e.currentTarget.closest(".be-menu--sub")?.previousElementSibling;
			if (!(opener instanceof HTMLElement)) return;
			// one level only, so a nested Escape does not put the whole menu away
			e.preventDefault();
			e.stopPropagation();
			opener.focus();
		}
	};

	const item = (
		<button
			type="button"
			{...mark}
			aria-haspopup={caret || undefined}
			aria-expanded={child != null ? entered : undefined}
			disabled={disabled}
			onClick={invoke}
			onKeyDown={keys}
			{...rest}
			{...be(
				"be-menu__item",
				icon != null && "be-menu__item--icon",
				blink === false && "be-menu__item--no-blink",
				selected && "is-selected",
				marked && "is-marked",
				caret && "has-submenu",
			)}
		>
			{icon != null && <span className="be-menu-icon">{icon}</span>}
			{pixelText(children, "be-menu__label")}
			{shortcut !== undefined &&
				(isShortcut(shortcut) ? <ShortcutKeys shortcut={shortcut} disabled={disabled} /> : pixelText(shortcut, "be-menu__shortcut"))}
		</button>
	);

	if (child == null) return item;

	// a <div role="menu"> of buttons cannot nest inside the <button> it belongs to
	return (
		<div className="be-menu__nest" role="none" ref={nest} onFocus={() => setEntered(true)} onBlur={() => setEntered(false)}>
			{item}
			<BeMenu className="be-menu--sub">{child}</BeMenu>
		</div>
	);
});

export function BeMenuSeparator() {
	return <hr className="be-menu__separator" />;
}
